// ToDo: WW-7459 Move to shared shadow folder
import styles from '../../../../../../../../erp/components/tab-panels/positions/components/edit-table/components/TableCell/TableCell.module.scss';

import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton } from '@mui/material';
import clsx from 'clsx';
import React, { CSSProperties, forwardRef } from 'react';
import { TableCommonProps } from 'react-table';

import { AddServiceContractPosTypeOfPos } from '@work4all/models/lib/Enums/AddServiceContractPosTypeOfPos.enum';
import { ServiceContractPositionKind } from '@work4all/models/lib/Enums/ServiceContractPositionKind.enum';

import { RowSizeMode } from '../../../../../../../../../../../../settings/settings';
import { KINDS_TO_DISABLE_PICKERS as KINDS_TO_DISABLE_BORDERS } from '../../../hooks/use-positions-columns';
import { EditTablePositionKind } from '../../types';

export type ITableCellProps = TableCommonProps & {
  onDoubleClick?: React.MouseEventHandler<HTMLDivElement>;
  onClick?: (element: HTMLElement | React.MouseEvent<HTMLElement>) => void;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  children?: React.ReactNode;
  id?: string;
  kind?: EditTablePositionKind;
  shouldWrap?: boolean;
  rowInEditMode?: boolean;
  rowSizeMode?: RowSizeMode;
  expandable?: boolean;
  innerRef?: (instance: HTMLElement) => void;
  toggleExpanded?: () => void;
  expanded?: boolean;
  hasBorder?: {
    vertical: boolean;
    horizontal: boolean;
  };
};

export const TableCell = forwardRef<HTMLDivElement, ITableCellProps>(
  function TableCell(props, ref) {
    const {
      className,
      id,
      kind,
      style,
      shouldWrap,
      rowSizeMode,
      rowInEditMode,
      expandable,
      innerRef,
      toggleExpanded,
      expanded,
      hasBorder,
      ...rest
    } = props;

    const singleLine =
      rowSizeMode === 'SINGLE-AUTO' ? !rowInEditMode : rowSizeMode !== 'AUTO';
    const css = renderCellByType({ id: id as Columns, kind });

    if (shouldWrap) {
      const lineClass =
        singleLine && !expanded ? styles['single-line'] : styles['multi-line'];
      return (
        <div
          ref={ref}
          style={{ ...style, ...css, position: 'relative' }}
          className={clsx(styles['table-cell'], className, {
            [styles['borderRight']]:
              hasBorder?.vertical && !KINDS_TO_DISABLE_BORDERS.includes(kind),
          })}
          {...rest}
        >
          <div
            ref={innerRef}
            className={clsx('inside-row', lineClass)}
            onDoubleClick={
              expandable && rowSizeMode === 'SINGLE'
                ? (e) => {
                    toggleExpanded();
                    props.onClick(e.currentTarget);
                  }
                : undefined
            }
          >
            {expandable && rowSizeMode === 'SINGLE' ? (
              <>
                {React.Children.map(rest.children, (child) => {
                  if (React.isValidElement(child)) {
                    return React.cloneElement(child, {
                      //@ts-expect-error wrong interface
                      shrinked: !expanded,
                      css,
                    });
                  }
                  return child;
                })}
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    toggleExpanded();
                    props.onClick(e.currentTarget.parentElement);
                  }}
                  className={styles.expandable}
                >
                  {expanded ? <CheckIcon /> : <EditIcon />}
                </IconButton>
              </>
            ) : (
              <>
                {React.Children.map(rest.children, (child) => {
                  if (React.isValidElement(child)) {
                    //@ts-expect-error wrong interface
                    return React.cloneElement(child, { css });
                  }
                  return child;
                })}
              </>
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        style={{ ...style, ...css }}
        className={clsx(styles['table-cell'], className, {
          [styles['borderRight']]:
            hasBorder?.vertical && !KINDS_TO_DISABLE_BORDERS.includes(kind),
        })}
        {...rest}
        children={
          <>
            {React.Children.map(rest.children, (child) => {
              if (React.isValidElement(child)) {
                //@ts-expect-error wrong interface
                return React.cloneElement(child, { css });
              }
              return child;
            })}
          </>
        }
      />
    );
  }
);

type Columns =
  | 'selection'
  | 'number'
  | 'longtext'
  | 'amount'
  | 'unit'
  | 'discount'
  | 'price'
  | 'purchasePrice';

interface RenderCellByTypeProps {
  id?: Columns;
  kind?: EditTablePositionKind;
}

interface CellDefinition {
  selection: CSSProperties;
  number: CSSProperties;
  longtext: CSSProperties;
  amount: CSSProperties;
  discount: CSSProperties;
  price: CSSProperties;
  purchasePrice: CSSProperties;
  name: CSSProperties;
}

export function renderCellByType(
  props: RenderCellByTypeProps
): CSSProperties | undefined {
  const { kind, id } = props;

  if (!kind) return undefined;

  const standard = {
    selection: { color: 'var(--03)' },
    amount: { color: 'var(--03)' },
    discount: { color: 'var(--03)' },
    price: {},
    purchasePrice: {},
  };

  const headlineStyle = {
    selection: { color: 'var(--03)' },
    number: { fontWeight: '700' },
    longtext: { fontWeight: '700' },
    amount: { color: 'var(--03)' },
    discount: { color: 'var(--03)' },
    price: {
      fontWeight: '700',
    },
    purchasePrice: {
      fontWeight: '700',
    },
  };

  const definitions: Record<
    ServiceContractPositionKind | AddServiceContractPosTypeOfPos,
    Partial<CellDefinition>
  > = {
    UEBERNEHMEN_MIT_WARTUNGSSUMME: standard,
    UEBERNEHMEN_MIT_E_PREIS_AUS_WARTUNGS_OBJEKT: standard,
    UEBERNEHMEN_OHNE_PREIS: standard,
    UEBERNEHMEN_MIT_ARTIKELPREIS: standard,
    TEXTZEILE: standard,
    UEBERSCHRIFT: headlineStyle,
    NICHT_UEBERNEHMEN: standard,
    DEFAULT: standard,
    HEADLINE: headlineStyle,
    TEXTLINE: standard,
  };

  const def = definitions[kind];
  const cellDef = def[id];
  return cellDef;
}
